import PropTypes from 'prop-types';
import { ErrorCode } from 'react-dropzone';

import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

import { fileData } from '../file-thumbnail';
import { fData } from '../../utils/format-number';

// ----------------------------------------------------------------------

/** A size limit as the seller reads it: 5242880 bytes is "5 MB". */
function sizeLimit (maxSize) {
  return `${Number((maxSize / (1024 * 1024)).toFixed(1))} MB`;
}

/**
 * What the seller reads about one reason a drop zone turned a file away, in place of the drop zone's own words
 * ("File is larger than 5242880 bytes").
 *
 * @param {{code: string}} error - one of the drop zone's reasons
 * @param {number} [maxSize] - the drop zone's size limit in bytes
 * @returns {string}
 */
export function rejectionSentence ({ code }, maxSize) {
  switch (code) {
    case ErrorCode.FileTooLarge:
      return maxSize ? `Too big. Pick one under ${sizeLimit(maxSize)}.` : 'Too big. Pick a smaller one.';
    case ErrorCode.FileInvalidType:
      return "This type of file can't be used here.";
    case ErrorCode.TooManyFiles:
      return 'Pick just one file.';
    default:
      return "This file can't be used.";
  }
}

export default function RejectionFiles ({ fileRejections, maxSize }) {
  if (!fileRejections.length) {
    return null;
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        py: 1,
        px: 2,
        mt: 3,
        textAlign: 'left',
        borderStyle: 'dashed',
        borderColor: 'error.main',
        bgcolor: (theme) => alpha(theme.palette.error.main, 0.08),
      }}
    >
      {fileRejections.map(({ file, errors }) => {
        const { path, name, size } = fileData(file);

        return (
          <Box key={path} sx={{ my: 1 }}>
            <Typography variant="subtitle2" noWrap>
              {name} - {size ? fData(size) : ''}
            </Typography>

            {errors.map((error) => (
              <Box key={error.code} component="span" sx={{ typography: 'caption' }}>
                - {rejectionSentence(error, maxSize)}
              </Box>
            ))}
          </Box>
        );
      })}
    </Paper>
  );
}

RejectionFiles.propTypes = {
  fileRejections: PropTypes.array,
  maxSize: PropTypes.number,
};
